import S, { DataSignal } from 's-js';
import { SArray as ArraySignal, lift } from 's-array';
import * as moment from 'moment';
import * as _ from 'underscore';
import * as Promise from 'bluebird';
import { IAjax } from 'lib/Ajax';
import { CachedModelLoader } from 'lib/CachedModelLoader';
import { AutoSavingDraftObject, AutoSaver } from 'lib/autoSave';
import { ValidationWatcher } from 'lib/validationWatcher';
import TZDate from 'lib/TZDate';
import { TZUtils } from 'lib/tzutils';
import { tzmomentMod } from 'lib/tzmoment';
import { Pollster, PollsterStartupBehavior } from 'lib/pollster';
import uniqueIndex, { UniqueIndex } from 'lib/uniqueIndex';
import UOM from 'lib/UOM';
import { ConvertedMeasurement } from 'lib/ConvertedMeasurement';
import { MeasurementInFeetAndInches } from 'lib/MeasurementInFeetAndInches';
import { groupBy } from 'lib/groupBy';

import { Entrypoints } from 'core/app/entrypoints';

import { PCCache } from 'data/PCCache';

import { AcknowledgePilotJobOrdersRequest } from 'data/models/AcknowledgePilotJobOrdersRequest';
import { AgencyCode } from 'data/models/AgencyCode';
import { BerthReservation } from 'data/models/BerthReservation';
import { DispatchDashboard as ServerDispatchDashboard, DispatchDashboardOptions } from 'data/models/DispatchDashboard';
import { DispatchDashboardTab } from 'server/enums/DispatchDashboardTab';
import { DispatchEvent } from 'data/models/DispatchEvent';
import { Issue } from 'data/models/Issue';
import { IssueSeverity } from 'server/enums/Issue';
import { IssueType } from 'data/models/IssueType';
import { ItineraryPoint } from 'data/models/ItineraryPoint';
import { NullableMeasurementLike } from 'data/models/Measurement';
import { Organization } from 'data/models/Organization';
import { PCUser } from 'data/models/PCUser';
import { PierSettings } from 'data/models/PierSettings';
import { PilotJobOrder, EffectiveDraft } from 'data/models/PilotJobOrder';
import { PilotJobOrderDetails as ServerPilotJobOrderDetails, PilotJobOrderDetailsOptions } from 'data/models/PilotJobOrderDetails';
import { PortCall } from 'data/models/PortCall';
import { Vessel } from 'data/models/Vessel';
import { VesselSide, VesselSideDisplayName } from 'server/enums/BerthReservation';

import { toCSVBlob } from 'common/services/csvExport';
import { serializeDate } from 'common/services/routeParams';

import { AddDispatchEvent, AddDispatchEventParams } from './adddispatchevent';
import { AddPilotJobOrder, AddPilotJobOrderParams } from './addpilotjoborder';
import { DispatchEventDetails, DispatchEventDetailsParams } from './dispatcheventdetails';
import { PilotJobOrderPreferences, PilotJobOrderPreferencesParams } from './pilotjoborderpreferences';

import { DateRangeSelector, DateRangeSelectorParams, DateRange } from 'portcall/daterangeselector/model'
import { serializeDateRange } from 'portcall/daterangeselector/routeParams';
import { PilotJobOrderDetails, PilotJobOrderDetailsParams, BackPage } from 'pilots/pilotjoborderdetails/model';
import { PilotJobOrderDetailsCache } from 'pilots/pilotjoborderdetails/cache';

import { CSVColumns } from './csvExport';

const DASHBOARD_REFRESH_SECONDS = 30;

// Length of time to show in the "Rolling" export option.
export const ROLLING_EXPORT_HOURS = 24;

// TH 01/23/2017 -- Date format as a Momentjs template string.
// NOTE: This constant needs to agree w/ internalDateFormat below
export const INTERNAL_DATE_FORMAT = 'YYYY-MM-DD';

// Max number of items to show in the browser UI in search/billing worklist tabs. We
// cap this to conserve browser memory (b/c rendering every DOM node can blow up memory
// for large searches).
export const MAX_SEARCH_RESULTS_SHOWN = 700;

// TH: Using native Date methods instead of Momentjs to speed up indexing by date
export function internalDateFormat(date : TZDate) {
    return date.toJSON().substr(0, 10); // YYYY-MM-DD
}

// TH 04/12/2017 -- The name of this tab got changed from History to Search.
// But I'm still gonna leave this localStorage key as is to preserve any previously saved searches.
const filterStorageKey = 'portcall.dispatchdashboard.historical.filter';

// Ordering constants used to order dashboard items (PJOs or DispatchEvents) that have the same start time.
// Order goes IN > OUT > SHIFT > DispatchEvent
const enum DashboardItemOrder {
    PjoIn    = 0,
    PjoOut   = 1,
    PjoShift = 2,
    Event    = 3
};

interface DashboardItemSort {
    Date : () => TZDate,
    ItemOrder : () => DashboardItemOrder;
    Unique : () => number;
}

type DashboardItem = PilotJobOrderViewModel | DispatchEventViewModel;

function ColumnNames(settings : PierSettings) {
    return {
        MovementType: 'Type',
        EstimatedTime: 'Est.',
        ActualTime: 'Time',
        VesselName: 'Vessel',
        Location: 'Location',
        AgencyCode: 'Agent',
        GrossTonnage: 'Tonnage',
        Length: `Length (${settings.DefaultLengthUOM()})`,
        Beam: `Beam (${settings.DefaultLengthUOM()})`,
        DeepDraft: `Draft (${settings.DefaultDepthUOM()})`,
        AirDraft: `Air (${settings.DefaultDepthUOM()})`,
        TugCompany: 'Tug Co',
        SideTo: 'Side',
        Pilot: 'Pilot'
    };
}

interface DispatchDashboardCommonParams {
    id : number;
    pjoId : number;
    dispatchEventId : number;
    pjoFlip : BackPage | null;
}

interface NewTabParams extends DispatchDashboardCommonParams {
    tab : DispatchDashboardTab.New;
}

interface MovementsTabParams extends DispatchDashboardCommonParams {
    tab : DispatchDashboardTab.Movements;
}

interface SearchTabParams extends DispatchDashboardCommonParams {
    tab : DispatchDashboardTab.Search;
    searchParams : DispatchDashboardSearchParams;
}

interface BillingWorklistTabParams extends DispatchDashboardCommonParams {
    tab : DispatchDashboardTab.BillingWorklist;
    searchParams : DispatchDashboardSearchParams;
}

interface DispatchDashboardSearchParams {
    range : DateRange;
    from? : TZDate;
    to? : TZDate;
    filter? : string;
}

interface AddPJOPopupParams {
    from : TZDate | null;
    to : TZDate | null;
    portCallId? : number;
    template? : PilotJobOrder;
}

export type DispatchDashboardParams = NewTabParams | MovementsTabParams | SearchTabParams | BillingWorklistTabParams;

type DashboardItemSelection = { pjoId : number, dispatchEventId? : undefined } | { pjoId? : undefined, dispatchEventId : number };

export function DispatchDashboard(
    loader : CachedModelLoader<ServerDispatchDashboard, DispatchDashboardOptions>,
    pjoLoader : CachedModelLoader<ServerPilotJobOrderDetails, PilotJobOrderDetailsOptions>,
    pjoCache : PilotJobOrderDetailsCache,
    ajax : IAjax,
    entrypoints : Entrypoints,
    data : PCCache,
    tzutils : TZUtils,
    pierSettings : PierSettings,
    dateRangeSelectorModel : (params : DateRangeSelectorParams) => DateRangeSelector,
    addDispatchEventModel : (params : AddDispatchEventParams) => AddDispatchEvent,
    dispatchEventDetailsModel : (params : DispatchEventDetailsParams) => DispatchEventDetails,
    addPilotJobOrderModel : (params : AddPilotJobOrderParams) => AddPilotJobOrder,
    pjoDetailsModel : (params : PilotJobOrderDetailsParams) => PilotJobOrderDetails,
    pjoPrefsModel : (params : PilotJobOrderPreferencesParams) => PilotJobOrderPreferences,
    params : DispatchDashboardParams
) {
    let initialLoad = true;

    const
        { id, pjoId, dispatchEventId, tab } = params,
        searchParams = params.tab === DispatchDashboardTab.Search || params.tab === DispatchDashboardTab.BillingWorklist ?
            params.searchParams : null,
        dateRangeSelector = searchParams && dateRangeSelectorModel({
            range: searchParams.range,
            from: searchParams.from,
            to: searchParams.to,
            allowedRanges: tab === DispatchDashboardTab.BillingWorklist ? [
                DateRange.AllDates,
                DateRange.ThisWeek,
                DateRange.LastWeek,
                DateRange.ThisMonth,
                DateRange.LastMonth,
                DateRange.ThisYear,
                DateRange.LastYear,
                DateRange.Custom
            ] : undefined
        }),
        filter = searchParams && S.data(searchParams.filter || localStorage[filterStorageKey] as string | undefined || ''),
        draftModelKey = S(() : DispatchDashboardOptions => ({
            Id: id,
            Tab: tab,
            From: dateRangeSelector && dateRangeSelector.FromDate().toJSON(),
            To: dateRangeSelector && dateRangeSelector.ToDate().toJSON(),
            // Convert empty filter to null to account for the fact that our WebAPI controller sees null when we pass it an empty
            // query string value.
            SearchQuery: filter && filter() ? filter() : null,
            // TH 10/20/2017 -- Seed navbar too, so it can load w/ default values as soon as possible
            Navbar: { Id: id }
        })),
        // NOTE: Intentionally an S.data and NOT an S.value, so that setting the model key
        // always triggers a refresh of data, even w/ no change to its value.
        modelKey = S.data(S.sample(draftModelKey)),
        modelKeyDirty = () =>
            draftModelKey().From !== modelKey().From ||
            draftModelKey().To !== modelKey().To ||
            draftModelKey().SearchQuery !== modelKey().SearchQuery,
        modelLoader = S(() => {
            const initial = initialLoad;
            initialLoad = false;
            // Just landing on page, we'll tolerate slightly stale data. But if user explicitly
            // triggers a search, we'll always send a new request.
            return loader(modelKey(), initial ? 30 : 0);
        }),
        model = () => modelLoader().model,
        // TH 01/23/2017 -- Basing this on the modelLoader computation - desired effect is
        // that this triggers when the modelKey changes (i.e. when user changes date selection in historical mode)
        // but not when we poll in non-historical mode.
        modelLoading = () => modelLoader().loading(),
        pilotAgency = () => model().PilotAgency();

    // Store filter whenever user executes their new search
    filter && S.on(modelKey, () => {
        localStorage[filterStorageKey] = filter();
    });

    // Prevent searches that we think are overly broad and likely to crash the browser
    const
        searchSelectionIsInvalid = () =>
            !!dateRangeSelector &&
            !!filter &&
            // Invalid if text filter is short or missing, and...
            filter().trim().length < 4 &&
            // ...selected date range > 1 yr (w/ a couple days forgiveness to allow leap
            // years plus general fuzziness).
            +dateRangeSelector.ToDate() - +dateRangeSelector.FromDate() > 367 * 24 * 60 * 60 * 1000,
        showInvalidSearchMsg = S.value(false);

    const selectedItemKey = S.data<DashboardItemSelection | null>(pjoId ? { pjoId } : dispatchEventId ? { dispatchEventId } : null);

    const
        showAddDispatchEventModal = S.data(false),
        dispatchEventsUrl = () => model().PilotAgency() && model().PilotAgency()!.DispatchEventsUrl() || '',
        onAddDispatchEvent = (event : DispatchEvent) => S.freeze(() => {
            // TH 01/26/2017 -- The AddDispatchEvent component takes care of adding the new event
            // to the PCCache. But that still doesn't update the DispatchEvents collection in our model
            // b/c we use a QueryResultsRelationship. So we need to update that collection too.
            //
            // TH: 5/2/2016 -- This adds the new dispatch event to the page whether or not it fits
            // in the current date range (next two weeks for Movements view, range determined by dropdown and date inputs for History view).
            // Is that OK? Alternative would be to save the new DispatchEvent in the Db but not show it on the page if it isn't w/in the specified date range.
            //
            // TH 01/26/2017 -- One consequence is that if you add an event outside the specified date range for the page,
            // you'll only see it on the page till the next poll request, after which it'll fall off again...
            model().DispatchEvents.push(event);
            showAddDispatchEventModal(false);
        }),
        addDispatchEvent = S(() => showAddDispatchEventModal() ? addDispatchEventModel({
            url: dispatchEventsUrl,
            canManageBills: () => model().CanManageBills(),
            onAddDispatchEvent,
            onCancel: () => showAddDispatchEventModal(false)
        }) : null);

    const
        addPilotJobOrderPopupParams = S.value<AddPJOPopupParams | null>(null),
        addPilotJobOrder = S(() => {
            const params = addPilotJobOrderPopupParams();
            if (!params) return null;
                const
                    from = params.from ? params.from : params.to ?
                        tzmomentMod(tzutils, params.to, m => m.subtract(2, 'weeks')) :
                        tzmomentMod(tzutils, tzutils.now(), m => m.startOf('day')),
                    to = params.to ? params.to :
                        tzmomentMod(tzutils, from, m => m.add(2, 'weeks'));

                return addPilotJobOrderModel({
                    pilotAgencyId: id,
                    from,
                    to,
                    portCallId: params.portCallId || null,
                    template: params.template,
                    onAddPilotJobOrder,
                    onCancel: () => addPilotJobOrderPopupParams(null)
                })
        });

    const showRollingWindowOnly = S.data(false),
        // TH 02/15/2017 -- Clock for updating the "Rolling 12 hours" export option in the Movements tab.
        // I figure every 60 secs is plenty often.
        // Also, we don't currently need a clock for this, since we only show the rolling
        // window for a split second (just long enough to call window.print).
        // But the clock puts us in good shape if we ever decide to turn the rolling
        // window into a toggleable view state.
        rollingClock = S(() => showRollingWindowOnly() ? tzutils.clock(60 * 1000) : null);

    const
        showActionsMenu = S.data(false),
        agencyCodes = lift(() => pilotAgency() ? pilotAgency()!.AgencyCodes() : []),
        agencyCodesByOrg = uniqueIndex(agencyCodes, ac => ac.CodedOrganizationId()),
        organizationModel = (org : Organization) => OrganizationViewModel(org, agencyCodesByOrg),
        agentModel = (u : PCUser) => AgentViewModel(u, organizationModel),
        vesselModel = (v : Vessel) => VesselViewModel(v, pierSettings),
        portCallModel = (pc : PortCall) => PortCallViewModel(pc, pierSettings, agentModel);

    const
        pilotJobOrders = lift(() => model().JobOrders())
            .map(pjo => PilotJobOrderViewModel(pjo, ajax, data, pierSettings, portCallModel, ItineraryPointViewModel, vesselModel, pjoPrefsModel, selectedItemKey)),
        dispatchEvents = lift(() => model().DispatchEvents())
            .map(de => DispatchEventViewModel(de, ajax, data, model, selectedItemKey)),
        dispatchEventsById = uniqueIndex(dispatchEvents, de => de.Model.Id()),
        selectedDispatchEvent = S(() => {
            const key = selectedItemKey(),
                event = key && key.dispatchEventId ? dispatchEventsById(key.dispatchEventId)() : null;
            return event ? dispatchEventDetailsModel({
                event,
                canEdit: () => model().CanManageDispatchEvents(),
                canManageBills: () => model().CanManageBills()
            }) : null;
        });

    let dashboardItems = (pilotJobOrders as ArraySignal<DashboardItem>)
        .concat(dispatchEvents)
        .sort(dashboardItemOrder);

    // TH 05/16/2017 -- Roll items off of the BillingWorklist tab as user toggles Billable off or Approves for Export.
    // We could also filter for Pilot Complete and that Events are in the past here.
    // But we don't currently expect those to be the main actions for removing items from this tab.
    if (tab === DispatchDashboardTab.BillingWorklist) dashboardItems = dashboardItems
        .filter(item => item.Model.Billable() && item.Model.ApprovalOfCharges() === null);

    dashboardItems = dashboardItems
        // TH 02/15/2017 -- Apply extra filter for "Rolling" print option.
        // Note that comparing dates as numbers (by using the + operator) is apparently a performance optimization in Chrome.
        // Was seeing "Not optimized: Unsupported non-primitive compare" when I compared dates directly.
        .filter(item => !rollingClock() || 
            (+item.DashboardSort.Date() < +tzmomentMod(tzutils, rollingClock()!.now(), m => m.add(ROLLING_EXPORT_HOURS, 'hours'))
             // aah 20171017 - don't include completed movements in rolling display
             && (item.ItemType === 'Event' || item.Model.End.Actual() === null)));

    const displayedDashboardItems = lift(() =>
        tab === DispatchDashboardTab.Search || tab === DispatchDashboardTab.BillingWorklist ?
        dashboardItems().slice(0, MAX_SEARCH_RESULTS_SHOWN) :
        dashboardItems()
    );

    const resultSetIsTruncated = () => displayedDashboardItems().length < dashboardItems().length;

    const dayViewModels = groupBy(displayedDashboardItems, item => internalDateFormat(item.DashboardSort.Date()))
        // NOTE: Even though we keep the dashboardItems collection above in sort order,
        // the groupBy call above does NOT guarantee that sort order will be preserved.
        // groupBy uses nonuniqueIndex internally, and nonuniqueIndex doesn't respond
        // to changes in the ordering of the input ArraySignal after it's been
        // initialized. So groupBy doesn't either. The problem shows up if you, say,
        // edit PJO dates after the page has loaded such that ordering of PJOs changes.
        // Hence why we re-sort again before passing to DashboardDayViewModel.
        .map(({ key: date, items }) => DashboardDayViewModel(date, items.sort(dashboardItemOrder)));

    let pjoFlip = params.pjoFlip;
    const canAcknowledgeIds = lift(() => model().CanAcknowledgeIds()),
        canAcknowledgeLookup = uniqueIndex(canAcknowledgeIds, _id => _id),
        canAcknowledgePJO = (pjo : PilotJobOrder) => canAcknowledgeLookup(pjo.Id())() !== null,
        debouncedPjoLoader = _.debounce(() => {
            const pjoId = selectedItemKey() && selectedItemKey()!.pjoId;
            if (pjoId) pjoLoader({ Id: pjoId }, 30000);
        // TH 04/14/2017 -- For now, the overlapping PJO details popup is really only a concern
        // on the New tab. So don't make the other tabs pay a penalty.
        }, tab === DispatchDashboardTab.New ? 500 : 0),
        pjoDetails = S(() => {
            const pjoId = selectedItemKey() && selectedItemKey()!.pjoId;
            if (pjoId) {
                const pjo = data.PilotJobOrders.update1(null, { Id: pjoId }),
                    serverPJODetails = pjoCache.PilotJobOrderDetailses.update1(null, { Id: pjoId });

                // aah 20171016: if this pjoId is for a deleted PJO, then pjo will be undefined, so return null
                if (pjo) {
                    // TH 04/14/2017 -- W/ the Acknowledge workflow for new PJOs, I implemented a feature
                    // where we auto select the next PJO in the list when a user hits Acknowledge.
                    // It's a nice UX for quickly Acknowledging several PJOs, but it made it really easy
                    // for users to send off a ton of overlapping requests to the PilotJobOrderDetails endpoint.
                    // So... Moving request initiation for the PJO details popup up to the DDash so we can
                    // put a debounce on it.
                    debouncedPjoLoader();

                    return pjoDetailsModel({
                        pjo,
                        model: serverPJODetails,
                        flip: pjoFlip,
                        onDelete: () => onDeletePilotJobOrder(pjo),
                        onReorder: () => onReorderPilotJobOrder(pjo),
                        onAddShift,
                        onAcknowledge: () => onAcknowledge(pjo),
                        canAcknowledge: () => canAcknowledgePJO(pjo),
                        corePermissions: () => model().CorePermissions()
                    });
                } else {
                    selectedItemKey(null);
                }
            }
            // TH 05/04/2017 -- The pjoFlip param only matters for the initial load of the dashboard.
            // Null it out after that so that we don't influence subsequent PJO popups.
            pjoFlip = null;
            return null;
        });

    const
        acknowledgementRequest = new AcknowledgePilotJobOrdersRequest(),
        acknowledgementClient = ajax.Url<AcknowledgePilotJobOrdersRequest>(entrypoints.AcknowledgePilotJobOrders, { useCache: true }),
        // TH 10/21/2017 -- A count of how many acknowledgement requests are either pending
        // (b/c the user has hit acknowledge and our debounce timer is running) or in flight.
        // B/c of how acknowledgement requests are serialized, we expect this to only ever be
        // 0, 1, or 2.
        pendingAckRequestCount = S.data(0);

    let oldAckIdCount = 0;

    S(() => {
        const newAckIdCount = acknowledgementRequest.PilotJobOrderIds().length;
        if (oldAckIdCount === 0 && newAckIdCount > 0) {
            // TH 10/21/2017 -- Increment pendingAckRequestCount as soon as we push an Id.
            pendingAckRequestCount(S.sample(pendingAckRequestCount) + 1);
        }
        oldAckIdCount = newAckIdCount;
    });

    // TH 04/17/2017 -- AutoSave PJO acknowledgements.
    // This has the effect of debouncing and serializing these requests.
    AutoSaver(acknowledgementRequest.PilotJobOrderIds, () => {
        if (acknowledgementRequest.PilotJobOrderIds().length > 0) {
            const req = acknowledgementClient.Post(acknowledgementRequest);
            acknowledgementRequest.PilotJobOrderIds([]);
            // TH 10/21/2017 -- Decrement pendingAckRequestCount after we get a response.
            return req.then(() => {}).finally(() => pendingAckRequestCount(Math.max(pendingAckRequestCount() - 1, 0)));
        } else {
            return Promise.resolve();
        }
    });

    const
        csvItems = dashboardItems.filter(i => i.ItemType === "PJO") as ArraySignal<PilotJobOrderViewModel>,
        // NOTE: A plain lambda and NOT an S(() => ...) computation. We'll only construct the
        // CSV payload when the user clicks the CSV export link. That makes that action slightly
        // slower, but avoids the memory and CPU overhead of keeping our CSV payload up-to-date
        // as data loads or is edited.
        csvData = () => toCSVBlob(csvItems(), CSVColumns(pierSettings));

    // TH 05/16/2017 -- Links for navigating to/from Billing Worklist.
    // My thought is to preserve as much of the current page filters as possible.
    // Update these whenever the user executes their new search.
    const viewBillingWorklistLink = S.on(modelKey, () => {
            const
                // TH 05/16/2017 -- ThisMonth is default for Search tab, so we'll treat that as unset
                range = dateRangeSelector && dateRangeSelector.Range() !== DateRange.ThisMonth ?
                    dateRangeSelector.Range() : undefined,
                fromDate = dateRangeSelector && dateRangeSelector.Range() === DateRange.Custom ?
                    dateRangeSelector.FromDate() : undefined,
                toDate = dateRangeSelector && dateRangeSelector.Range() === DateRange.Custom ?
                    dateRangeSelector.ToDate() : undefined,
                _filter = filter ? filter() : undefined;
            return `#!dispatchdashboard?id=${id}&tab=${DispatchDashboardTab.BillingWorklist}`
                + (range !== undefined ? `&range=${serializeDateRange(range)}` : '')
                + (fromDate !== undefined ? `&from=${serializeDate(fromDate)}` : '')
                + (toDate !== undefined ? `&to=${serializeDate(toDate)}` : '')
                + (_filter ? `&filter=${encodeURIComponent(_filter)}` : '');
        }),
        backToFullSearchLink = tab === DispatchDashboardTab.BillingWorklist ? S.on(modelKey, () => {
            const
                // TH 05/16/2017 -- AllDates is default for BillingWorklist tab, so we'll treat that as unset.
                // It's also not a legal option for the Search tab.
                range = dateRangeSelector!.Range() !== DateRange.AllDates ?
                    dateRangeSelector!.Range() : undefined,
                fromDate = dateRangeSelector!.Range() === DateRange.Custom ?
                    dateRangeSelector!.FromDate() : undefined,
                toDate = dateRangeSelector!.Range() === DateRange.Custom ?
                    dateRangeSelector!.ToDate() : undefined,
                _filter = filter!();
            return `#!dispatchdashboard?id=${id}&tab=${DispatchDashboardTab.Search}`
                + (range !== undefined ? `&range=${serializeDateRange(range)}` : '')
                + (fromDate !== undefined ? `&from=${serializeDate(fromDate)}` : '')
                + (toDate !== undefined ? `&to=${serializeDate(toDate)}` : '')
                + (_filter ? `&filter=${encodeURIComponent(_filter)}` : '');
        }) : null;

    const
        billingSystems = () => model().PilotAgency() && model().PilotAgency()!.Organization() ?
            model().PilotAgency()!.Organization()!.BillingSystems() : [],
        // TH 05/09/2017 -- As in other spots, just assume there's at most one BillingSystem for now.
        billingSystem = S(() => billingSystems().length > 0 ? billingSystems()[0] : null);

    // Start polling
    // UPDATE: 4/12/2017 -- But only poll in New and Movements tabs
    if (tab === DispatchDashboardTab.New || tab === DispatchDashboardTab.Movements) {
        const pollster = new Pollster(() => loader(modelKey()).task, DASHBOARD_REFRESH_SECONDS * 1000);

        // TH 05/04/2017 -- Pause polling while the PJO Details or Dispatch Event details are open.
        // This is to minimize the possibility of race conditions from editing data while polling for
        // the same data. This problem is better than it used to be since AH's change to populate
        // the response date and pass it through to the DataCache.
        // But I'm still noticing it often enough in development that I think users might hit it.
        // One spot that I'm leaving broken is the Remarks hide/show button -- it's still the case
        // that polling can sometimes clobber users' edits to hide/show Remarks for a PJO.
        // I figure that's not a critical piece of data -- I'm much more concerned about users
        // editing key PJO fields like times, air/deep draft, and billing data and then seeing their
        // edits disappear.
        //
        // TH 10/20/2017 -- UPDATE: Also pause while any Acknowledge requests are pending or in flight.
        // Previously, if you acknowledged through to the end of the list, that'd dismiss the PJO
        // details popup and immediately trigger a poll request.
        // It'd also trigger an acknowledgement request, but that was behind a debounce of 300 ms.
        // So it'd be a race btwn the poll request and the ack request, and the poll request had a
        // 300 ms head start.
        // If the poll request read from the Db before the ack request made its changes, then
        // you'd see your acknowledged PJOs reappear on the page.
        // On my environment, that happened pretty much every time.
        S(initialPass => {
            if (!selectedItemKey() && pendingAckRequestCount() === 0) {
                // TH 05/04/2017 -- For initial construction of the view model, don't fire an immediate request
                // since there's already a request in flight. After that, do always fire off an immediate
                // request, say if we've just closed the PJO details popup.
                pollster.start(initialPass ? PollsterStartupBehavior.Lazy : PollsterStartupBehavior.Default);
            } else {
                pollster.stop();
            }
            return false;
        }, true);
    }

    return {
        Id: id,
        Tab: tab,
        Model: model,
        ModelLoading: modelLoading,
        ModelKey: () => modelKey(),
        ModelKeyDirty: modelKeyDirty,
        UpdateModelKey,
        ShowInvalidSearchMsg: showInvalidSearchMsg,
        Filter: filter,
        SelectedItemKey: selectedItemKey,
        SelectedDispatchEvent: selectedDispatchEvent,
        PrintPage,
        PrintRollingWindow,
        ShowActionsMenu: showActionsMenu,
        ToggleActionsMenu,
        DateRangeSelector: dateRangeSelector,
        Days: dayViewModels,
        ResultSetIsTruncated: resultSetIsTruncated,
        MenuAction,
        ShowAddPilotJobOrderModal,
        AddPilotJobOrder: addPilotJobOrder,
        ShowAddDispatchEventModal : showAddDispatchEventModal,
        AddDispatchEvent: addDispatchEvent,
        ColumnNames: () => ColumnNames(pierSettings),
        CSVData: csvData,
        ViewBillingWorklistLink: viewBillingWorklistLink,
        BackToFullSearchLink: backToFullSearchLink,
        PilotJobOrderDetails: pjoDetails,
        BillingSystem: billingSystem,
        // Allow child content to "take over" the entire page. This will cause most standard
        // content on this page to be hidden, including headers, the main page body, and
        // the PJO details and Dispatch Event popups
        AllowFullPageTakeOver: () =>
            !!pjoDetails() && pjoDetails()!.IsRequestingFullPageTakeOver()
    };

    function UpdateModelKey() {
        if (searchSelectionIsInvalid()) {
            showInvalidSearchMsg(true);
        } else {
            modelKey(draftModelKey());
        }
    }

    function ToggleActionsMenu() {
        showActionsMenu(!showActionsMenu());
    }

    function MenuAction(action : () => void) {
        return () => S.freeze(() => {
            action();
            showActionsMenu(false);
        });
    }

    function PrintPage() {
        window.print();
    }

    function PrintRollingWindow() {
        showRollingWindowOnly(true);
        // TH 02/15/2017 -- window.requestAnimationFrame to give DOM a chance to update
        // before printing.
        // This also guarantees that the two sets to showRollingWindowOnly happen in
        // different ticks, even when we run this function inside an S.freeze.
        window.requestAnimationFrame(() => {
            window.print();
            showRollingWindowOnly(false);
        });
    }

    function ShowAddPilotJobOrderModal() {
        addPilotJobOrderPopupParams({ from: model().From(), to: model().To() });
    }

    function onAddPilotJobOrder(pjo : PilotJobOrder) {
        S.freeze(() => {
            addPilotJobOrderPopupParams(null);
            // TH 01/26/2017 -- The AddPilotJobOrder takes care of adding the new PJO to the
            // PCCache (and also removing the old MissingPilotJobOrder from the query cache and from our
            // model's MissingPJOs collection).
            // But we still need to add the new PJO to our model's JobOrders collection b/c
            // it uses a QueryResultsRelationship.
            // That way the new PJO is visible before the next poll update.
            model().JobOrders.push(pjo);
            selectedItemKey({ pjoId: pjo.Id() });
        });
    }

    function onDeletePilotJobOrder(pjo : PilotJobOrder) {
        S.freeze(() => {
            // The PilotJobOrderDetails model takes care of updating the PCCache, but we still have to update our QueryResultsRelationship.
            model().JobOrders.remove(pjo);

            if (selectedItemKey() && selectedItemKey()!.pjoId === pjo.Id()) {
                selectedItemKey(null);
            }
        });
    }

    function onReorderPilotJobOrder(pjo : PilotJobOrder) {
        S.freeze(() => S.sample(() => {
            // Reordering a PJO just means opening the Add PJO popup with special filters and with the given PJO as a template
            const
                itinerary = pjo.PortCall() ? pjo.PortCall()!.Itinerary().slice().sort(ItineraryPoint.EstimatedOrder) : [],
                from = itinerary.length ? itinerary[0].Arrival.Estimated() : null,
                to = itinerary.length ? itinerary[itinerary.length - 1].Departure.Estimated() : null;

            addPilotJobOrderPopupParams({
                from,
                to,
                portCallId: pjo.PortCallId(),
                template: pjo
            });

            if (selectedItemKey() && selectedItemKey()!.pjoId === pjo.Id()) selectedItemKey(null);
        }));
    }

    function onAddShift(pjo : PilotJobOrder) {
        // TH 02/13/2017 -- AddShift from PJO details popup.
        // Select the new PJO and add it to the QueryResultsRelationship.
        S.freeze(() => {
            selectedItemKey({ pjoId: pjo.Id() });
            model().JobOrders.push(pjo);
        });
    }

    function onAcknowledge(pjo : PilotJobOrder) {
        S.freeze(() => {
            const key = selectedItemKey();

            if (key && key.pjoId === pjo.Id()) {
                // TH 02/18/2019 -- We now DO keep dashboardItems in sort order once again.
                // So perhaps this could be simplified. But leaving as is for now...
                // TH 04/14/2017 -- Try to select the next PJO in order.
                // This is a little tricky b/c we don't keep the dashboardItems collection in sorted order.
                // So instead loop through the dayViewModels collection and then through each day's DashboardItems.
                const currentDate = internalDateFormat(pjo.Start.Effective()),
                    days = dayViewModels();

                let nextPJO : PilotJobOrderViewModel | undefined,
                    foundCurrent = false;

                FIND_NEXT_PJO:
                for (let i = 0; i < days.length; i++) {
                    const day = days[i];
                    if (!foundCurrent && day.Date !== currentDate) continue;
                    const itemsForDay = day.DashboardItems();
                    for (let j = 0; j < itemsForDay.length; j++) {
                        const item = itemsForDay[j];
                        if (item.ItemType === 'PJO') {
                            if (!foundCurrent) {
                                if (item.Model.Id() === pjo.Id()) {
                                    foundCurrent = true;
                                }
                            } else {
                                nextPJO = item;
                                break FIND_NEXT_PJO;
                            }
                        }
                    }
                }
                // TH 04/12/2017 -- Keep rolling down the list as long as there's a nextPJO to select.
                // Otherwise, dismiss the details popup.
                selectedItemKey(nextPJO ? { pjoId: nextPJO.Model.Id() } : null);
            }
            model().JobOrders.remove(pjo);

            // TH 10/21/2017 -- Also decrement the navbar's count of new PJOs.
            if (model().Navbar()) {
                const navbarAckCount = model().Navbar()!.AcknowledgeCount();
                model().Navbar()!.AcknowledgeCount(Math.max(navbarAckCount - 1, 0));
            }

            // TH 04/14/2017 -- Now initiating PJO Acknowledgements request from the DDash.
            // That's so we can debounce these requests to avoid crashing the server w/ multiple overlapping requests.
            acknowledgementRequest.PilotJobOrderIds.push(pjo.Id());
        });
    }
}
export type DispatchDashboard = ReturnType<typeof DispatchDashboard>;

function DashboardDayViewModel(
    date : string,
    items : ArraySignal<DashboardItem>
) {
    return {
        Date: date,
        DateMoment: moment(date, INTERNAL_DATE_FORMAT),
        DashboardItems: items
    };
}
export type DashboardDayViewModel = ReturnType<typeof DashboardDayViewModel>;

// Sort dashboard items (PJOs and DispatchEvents) by (most significant to least significant):
//   1. Time (PJO.EffectiveStart or DispatchEvent.Start)
//   2. Item type (IN > OUT > SHIFT > DispatchEvent)
//   3. Id (to always have an unambiguous sort)
function dashboardItemOrder(a : PilotJobOrderViewModel | DispatchEventViewModel, b : PilotJobOrderViewModel | DispatchEventViewModel) {
    return (a.DashboardSort.Date().getTime() - b.DashboardSort.Date().getTime())
        || (a.DashboardSort.ItemOrder()      - b.DashboardSort.ItemOrder())
        || (a.DashboardSort.Unique()         - b.DashboardSort.Unique());
}

function DispatchEventViewModel(
    dispatchEvent : DispatchEvent,
    ajax : IAjax,
    data : PCCache,
    model : () => ServerDispatchDashboard,
    selectedItemKey : DataSignal<DashboardItemSelection | null>
) {
    // TH: Auto-save and validation are for consumption by the dispatcheventdetails component
    const client = ajax.Url<DispatchEvent>(() => dispatchEvent.Url() || ''),
        draft = AutoSavingDraftObject(dispatchEvent, client, [
            'Billable',
            'Description',
            'QBTracking.Customer',
            'Start'
        ]),
        validation = ValidationWatcher(client),
        getIsSelected = () => !!selectedItemKey() && selectedItemKey()!.dispatchEventId === dispatchEvent.Id(),
        // TH 01/25/2017 -- Selected row performance optimization. See PilotJobOrderViewModel below.
        selected = S.value(S.sample(getIsSelected));

    S(() => selected(getIsSelected()));

    const dashboardSort : DashboardItemSort = {
        Date: draft.Start,
        ItemOrder: () => DashboardItemOrder.Event,
        Unique: draft.Id
    };

    return {
        Model: draft,
        Persisted: dispatchEvent,
        Validation: validation,
        Selected: selected,
        Delete,
        DashboardSort: dashboardSort,
        ItemType: 'Event' as 'Event'
    };

    // TH: Delete method is for consumption by the dispatcheventdetails component
    function Delete() {
        const id = draft.Id(),
            req = client.Delete();
        req.then(() => S.freeze(() => {
            data.DispatchEvents.update(req.Date, [], [id]);
            // TH 01/26/2017 -- We've updated the PCCache, but still have to update our
            // model's DispatchEvents collection since it uses a QueryResultsRelationship.
            model().DispatchEvents.remove(dispatchEvent);
            selectedItemKey(null);
        }));
    }
}
export type DispatchEventViewModel = ReturnType<typeof DispatchEventViewModel>;

function PilotJobOrderViewModel(
    jobOrder : PilotJobOrder,
    ajax : IAjax,
    data : PCCache,
    pierSettings : PierSettings,
    portCallModel : (pc : PortCall) => PortCallViewModel,
    itineraryPointModel : (pt : ItineraryPoint) => ItineraryPointViewModel,
    vesselModel : (v : Vessel) => VesselViewModel,
    pjoPrefsModel : (params : PilotJobOrderPreferencesParams) => PilotJobOrderPreferences,
    selectedItemKey : DataSignal<DashboardItemSelection | null>
) {
    const
        { draftPreferences } = pjoPrefsModel({ pjo: jobOrder }),
        remarksVisible = () => !!jobOrder.Remarks() && draftPreferences.RemarksVisible(),
        airDraftDisplay = displayDraft(jobOrder.EffectiveAirDraft),
        deepDraftDisplay = displayDraft(jobOrder.EffectiveDeepDraft),
        pilots = jobOrder.Pilots.orderBy(pa => pa.AssignmentOrder()),
        portcall = S(() => jobOrder.EffectivePortCall() && portCallModel(jobOrder.EffectivePortCall()!)),
        fromPoint = S(() => jobOrder.EffectiveFromPoint() && itineraryPointModel(jobOrder.EffectiveFromPoint()!)),
        toPoint   = S(() => jobOrder.EffectiveToPoint() && itineraryPointModel(jobOrder.EffectiveToPoint()!)),
        effectiveVessel = S(() => jobOrder.EffectiveVessel() && vesselModel(jobOrder.EffectiveVessel()!)),
        // Lengths rounds to whole foot or whole meter
        effectiveLength = new ConvertedMeasurement(jobOrder.EffectiveLength as NullableMeasurementLike, pierSettings.DefaultLengthUOM() || UOM.Foot, 0),
        // effectiveLength = new ConvertedMeasurement(jobOrder.EffectiveLength, pierSettings.DefaultLengthUOM() || UOM.Foot, 0),
        // Ignore conflicts for CNX PJOs...
        pjoIssues = lift(() => !jobOrder.Cancellation() ? jobOrder.Issues() : []),
        allConflicts = pjoIssues
            .concat(() => !jobOrder.Cancellation() && jobOrder.EffectiveFromPoint() && jobOrder.EffectiveFromPoint()!.BerthReservation() ? jobOrder.EffectiveFromPoint()!.BerthReservation()!.Issues() : [])
            .concat(() => !jobOrder.Cancellation() && jobOrder.EffectiveToPoint  () && jobOrder.EffectiveToPoint  ()!.BerthReservation() ? jobOrder.EffectiveToPoint  ()!.BerthReservation()!.Issues() : [])
            .filter(i => i.Severity() >= IssueSeverity.Conflict)
            .sort(Issue.ReversePrecedenceOrder),
        hasDraftConflict = pjoIssues.some(
            // TH 04/11/2017 -- NOTE: Intentionally ignoring any ReservationExceedsBerthDepth issues from either BerthReservation.
            // This drives styling on the Draft column, so our focus is just whether the PJO effective draft conflicts.
            i => i.Type() === IssueType.PilotJobOrderExceedsBerthDepth && i.Severity() >= IssueSeverity.Conflict
        ),
        getIsSelected = () => !!selectedItemKey() && selectedItemKey()!.pjoId === jobOrder.Id(),
        // TH 01/25/2017 -- I was seeing significant performance bottlenecks just selecting
        // PJOs and DispatchEvents when I had a large number of rows on screen.
        // E.g., was seeing 300+ ms to change the selected row in Chrome on my machine.
        // Traced it down to the fact that the old logic required React to rerender each row
        // every time the selected row changed -- just the calls to update props on each row and all its children
        // became expensive, even though very little was actually changing.
        // This approach using an S.value to hold the current selected status cuts down the time to about
        // 20 ms per selection change.
        selected = S.value(S.sample(getIsSelected));

    S(() => selected(getIsSelected()));

    const dashboardSort : DashboardItemSort = {
        Date: () => jobOrder.Start.Effective(),
        ItemOrder: () =>
            jobOrder.EffectiveToPoint() && jobOrder.EffectiveToPoint()!.BerthReservation() ?
                jobOrder.EffectiveFromPoint() && jobOrder.EffectiveFromPoint()!.BerthReservation() ?
                    DashboardItemOrder.PjoShift :
                    DashboardItemOrder.PjoIn :
                DashboardItemOrder.PjoOut,
        Unique: jobOrder.Id
    };

    return {
        Model: jobOrder,
        Pilots: pilots,
        Selected: selected,
        FromPoint: fromPoint,
        ToPoint: toPoint,
        EffectiveVessel: effectiveVessel,
        AllConflicts: allConflicts,
        HasDraftConflict: hasDraftConflict,
        RemarksVisible: remarksVisible,
        ToggleRemarksVisible: () => draftPreferences.RemarksVisible(!draftPreferences.RemarksVisible()),
        MovementTypeText,
        VesselNameText,
        LocationText,
        AgencyCodeText,
        GrossTonnageText,
        LengthText,
        BeamText,
        AirDraftText,
        DeepDraftText,
        TugCompanyText,
        SideToText,
        DashboardSort: dashboardSort,
        ItemType: 'PJO' as 'PJO',
        GetIsSelected: getIsSelected,
    };

    function displayDraft(pjoDraft : EffectiveDraft) {
        const uom = () => pierSettings.DefaultDepthUOM() || UOM.Foot;

        // Drafts round to feet and inches or to tenths of a meter
        return () => uom() === UOM.Foot ? {
                Type: 'inches' as 'inches',
                Display: new MeasurementInFeetAndInches(pjoDraft, 0)
            } : {
                Type: 'meters' as 'meters',
                Display: new ConvertedMeasurement(pjoDraft, uom(), 1)
            };
    }

    // TODO: Do we wanna update this text to align w/ our new movement type labeling on the homepage's
    // Traffic Schedule tab? There, we now use AREA for movement's btwn port-external locations and reserve
    // IN for movements from a port-external location to a port-internal one and OUT for movements from a
    // port-internal location to a port-external one.
    function MovementTypeText() {
        return (
            jobOrder.Cancellation() ?
                'CNX' :
                jobOrder.EffectiveToPoint() && jobOrder.EffectiveToPoint()!.BerthReservation() ?
                    jobOrder.EffectiveFromPoint() && jobOrder.EffectiveFromPoint()!.BerthReservation() ?
                        'SHIFT' :
                        'IN' :
                    'OUT'
        );
    }

    function VesselNameText() {
        return jobOrder.DisplayVesselIdentifier() || '';
    }

    function LocationText() {
        return LocationName(() => jobOrder.EffectiveFromPoint()) + ' to ' + LocationName(() => jobOrder.EffectiveToPoint());
    }

    function AgencyCodeText() {
        return (
            portcall() &&
            portcall()!.Agent() &&
            portcall()!.Agent()!.Organization() &&
            portcall()!.Agent()!.Organization()!.EffectiveAgencyCode() || ''
        );
    }

    function GrossTonnageText() {
        return effectiveVessel() ?
            effectiveVessel()!.Model.GrossTonnage.toString({ units: false }) : '--';
    }

    function LengthText() {
        return effectiveLength.toString({ units: false });
    }

    function BeamText() {
        return effectiveVessel() ?
            effectiveVessel()!.Beam.toString({ units: false }) : '--';
    }

    function AirDraftText() {
        const draft = airDraftDisplay();
        return draft.Type === 'inches' ? draft.Display.toString() : draft.Display.toString({ units: false});
    }

    function DeepDraftText() {
        const draft = deepDraftDisplay();
        return draft.Type === 'inches' ? draft.Display.toString() : draft.Display.toString({ units: false});
    }

    function TugCompanyText() {
        return jobOrder.TugCompany() ? jobOrder.TugCompany()!.Code() : '--';
    }

    function SideToText() {
        return toPoint() && toPoint()!.BerthReservation() ?
            toPoint()!.BerthReservation()!.SideToText() :
            fromPoint() && fromPoint()!.BerthReservation() ?
                fromPoint()!.BerthReservation()!.SideToText() :
                '';
    }

    function LocationName(pt : () => ItineraryPoint | null) {
        return pt() && pt()!.Location() && pt()!.Location()!.Name() || '';
    }
}
export type PilotJobOrderViewModel = ReturnType<typeof PilotJobOrderViewModel>;

function OrganizationViewModel(
    organization : Organization,
    agencyCodesByOrg : UniqueIndex<AgencyCode>
) {
    const code = agencyCodesByOrg(organization.Id),
        effectiveAgencyCode = () => {
            if (code()) return code()!.Code();
            const wordsInName = organization.Name().split(/\s+/),
                first3Words = wordsInName.slice(0, 3),
                firstLetters = first3Words.map(w => w.substr(0, 1));
            return firstLetters.join('').toUpperCase();
        };

    return {
        Model: organization,
        EffectiveAgencyCode: effectiveAgencyCode
    };
}
export type OrganizationViewModel = ReturnType<typeof OrganizationViewModel>;

function ItineraryPointViewModel(itineraryPoint : ItineraryPoint) {
    return {
        Model: itineraryPoint,
        BerthReservation: S(() => itineraryPoint.BerthReservation() && ReservationViewModel(itineraryPoint.BerthReservation()!))
    };
}
export type ItineraryPointViewModel = ReturnType<typeof ItineraryPointViewModel>;

function ReservationViewModel(reservation : BerthReservation) {
    return {
        Model: reservation,
        SideToText: () =>
            reservation.SideTo() === VesselSide.Starboard ? 'S' :
            reservation.SideTo() === VesselSide.Port      ? 'P' :
            reservation.SideTo() === VesselSide.SternTo   ? 'Ste' :
            VesselSideDisplayName[reservation.SideTo()]
    };
}
export type ReservationViewModel = ReturnType<typeof ReservationViewModel>;

function PortCallViewModel(
    portcall : PortCall,
    pierSettings : PierSettings,
    agentModel : (u : PCUser) => AgentViewModel
) {
    return {
        Model: portcall,
        Agent: S(() => portcall.Agent() && agentModel(portcall.Agent()!))
    };
}
export type PortCallViewModel = ReturnType<typeof PortCallViewModel>;

function AgentViewModel(
    agent : PCUser,
    organizationModel : (org : Organization) => OrganizationViewModel
) {
    return {
        Model: agent,
        Organization: S(() => agent.Organization() && organizationModel(agent.Organization()!))
    };
}
export type AgentViewModel = ReturnType<typeof AgentViewModel>;

function VesselViewModel(
    vessel : Vessel,
    pierSettings : PierSettings
) {
    return {
        Model: vessel,
        // Beam rounds to whole foot or whole meter
        Beam: new ConvertedMeasurement(vessel.Beam, pierSettings.DefaultLengthUOM() || UOM.Foot, 0)
    };
}
export type VesselViewModel = ReturnType<typeof VesselViewModel>;


// WEBPACK FOOTER //
// ./apps/pilots/dispatchdashboard/model.ts